import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import FreePaymentHandler from "@shared/modules/payments/handlers/FreePaymentHandler/FreePaymentHandler";
import WaitlistPaymentHandler from "@shared/modules/payments/handlers/WaitlistPaymentHandler/WaitlistPaymentHandler";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import SectionHeader from "../SectionHeader.web";
import { PaymentSectionWaitlistBaskets } from "./PaymentSectionWaitlistBaskets.web";
import { PaymentSectionFreeBaskets } from "./PaymentSectionFreeBaskets.web";
import { PaymentSectionRegularBaskets } from "./PaymentSectionRegularBaskets.web";
import type { CheckoutFormData } from "../../hooks/useCheckoutValidation";
import { VALIDATION_FIELDS } from "../../hooks/useCheckoutValidation";

/**
 * Payment Section
 *
 * This section manages payment validation across different basket types:
 * - Waitlist baskets (free, no payment required)
 * - Free baskets (promotional, no payment required)
 * - Regular baskets (paid, requires payment method and billing address)
 *
 * Uses React Hook Form context to update validation state in a unidirectional manner.
 */
export function PaymentSection({
  venue,
  publicBasket,
  allowRedirect,
  checkoutForms,
  enabledPaymentOptions,
  onBillingAddressChange,
}: {
  venue: PublicVenueSerializer | undefined;
  publicBasket: ReturnType<typeof usePublicBasket>;
  allowRedirect: boolean;
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  enabledPaymentOptions: Record<number, boolean>;
  onBillingAddressChange: (billingAddress: {
    info_home_address_country: string;
  }) => void;
}) {
  const { t } = useTranslation("checkout");
  const { manager } = usePaymentManager();
  const { setValue } = useFormContext<CheckoutFormData>();
  const [isValid, setIsValid] = useState(false);

  /**
   * Update form context when validation changes.
   * This maintains unidirectional data flow.
   */
  useEffect(() => {
    setValue(VALIDATION_FIELDS.payment, isValid);
  }, [isValid]);

  const isWaitlistBasket = manager.handler instanceof WaitlistPaymentHandler;
  const isFreeBasket = manager.handler instanceof FreePaymentHandler;
  const isRegularBasket = !isWaitlistBasket && !isFreeBasket;

  return (
    <Box>
      <Box>
        <SectionHeader
          sectionNumber={2}
          title={t("Payment")}
          isValid={isValid}
        />
      </Box>

      {/* WAITLIST baskets */}
      {isWaitlistBasket && (
        <Box>
          <PaymentSection.WaitlistBaskets
            publicBasket={publicBasket}
            checkoutForms={checkoutForms}
            onValid={() => setIsValid(true)}
            onInvalid={() => setIsValid(false)}
          />
        </Box>
      )}

      {/* FREE baskets */}
      {isFreeBasket && (
        <Box>
          <PaymentSection.FreeBaskets onValid={() => setIsValid(true)} />
        </Box>
      )}

      {/* "REGULAR" baskets */}
      {isRegularBasket && (
        <Box>
          <PaymentSection.RegularBaskets
            venue={venue}
            publicBasket={publicBasket}
            isBasketUpdating={publicBasket.isBasketUpdating}
            allowRedirect={allowRedirect}
            checkoutForms={checkoutForms}
            onValid={() => setIsValid(true)}
            onInvalid={() => setIsValid(false)}
            enabledPaymentOptions={enabledPaymentOptions}
            onBillingAddressChange={onBillingAddressChange}
          />
        </Box>
      )}
    </Box>
  );
}

PaymentSection.WaitlistBaskets = PaymentSectionWaitlistBaskets;
PaymentSection.FreeBaskets = PaymentSectionFreeBaskets;
PaymentSection.RegularBaskets = PaymentSectionRegularBaskets;
